import React, { Suspense, lazy, useEffect } from "react";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";

import { navigate, usePathname } from "./lib/router";
import { LandingPage } from "./app/landingPage/LandingPage";
import { LoginForm } from "./app/auth/LoginForm";
import { RegisterForm } from "./app/auth/RegisterForm";
import { AppShell } from "./app/components/AppShell";
import { AppointmentsPage } from "./app/appointments/AppointmentsPage";
import { EvolutionPage } from "./app/appointments/EvolutionPage";
import { ClientsPage } from "./app/clients/ClientsPage";
import NewClientPage from "./app/clients/NewClientPage";
import ClientDetailPage from "./app/clientDetails";
import EditClientPage from "./app/clientDetails/EditClientPage";
import { EmployeesPage } from "./app/employees/EmployeesPage";
import MetricsRouter from "./app/metrics";
import FinancialPage from "./app/financial/FinancialPage";
import PlanDetailsPage from "./app/recurringPlans/PlanDetailsPage";
import PaywallPage from "./app/paywall/PaywallPage";
import { BlogPage } from "./app/blog/BlogPage";
import BlogAdminPage from "./app/blog/BlogAdminPage";
import PostPage from "./app/blog/PostPage";
import BlogEditorPage from "./app/blog/BlogEditorPage";
import PrivacyTermsPage from "./app/legal/PrivacyTermsPage";
import { DeleteAccountPage } from "./app/deleteAccount/DeleteAccountPage";
import SetupCompanyPage from "./app/onboarding/SetupCompanyPage";

const SettingsPage = lazy(() => import("./app/settings/SettingsPage"));
const ResourcesPage = lazy(() => import("./app/settings/ResourcesPage"));
const SubscriptionPage = lazy(() => import("./app/subscription/SubscriptionPage"));
const WhatsappConfigPage = lazy(
  () => import("./app/settings/WhatsappConfigPage")
);
const EditCompanyPage = lazy(() => import("./app/settings/EditCompanyPage"));
const SpecialtiesListPage = lazy(() => import("./app/settings/SpecialtiesListPage"));
const ContractTemplatePage = lazy(() => import("./app/settings/ContractTemplatePage"));
const ScheduleExceptionsPage = lazy(() => import("./app/settings/ScheduleExceptionsPage"));
const InsuranceProvidersPage = lazy(() => import("./app/settings/InsuranceProvidersPage"));
const PublicSigningPage = lazy(() => import("./app/contracts/PublicSigningPage"));
const PublicReschedulePage = lazy(() => import("./app/appointments/PublicReschedulePage"));

export function App() {
  const path = usePathname();
  const signingToken = path.startsWith("/sign/") ? path.split("/")[2] : null;
  const rescheduleToken = path.startsWith("/remarcar/") ? path.split("/")[2] : null;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const src = params.get("source");
    if (src) {
      localStorage.setItem("source", src);
    }
  }, []);

  let screen: JSX.Element;
  if (signingToken) {
    screen = (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>}>
        <PublicSigningPage />
      </Suspense>
    );
  } else if (rescheduleToken) {
    screen = (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>}>
        <PublicReschedulePage />
      </Suspense>
    );
  } else if (path === "/") {
    screen = <LandingPage />;
  } else if (path === "/politica-e-termos") {
    screen = <PrivacyTermsPage />;
  } else if (path === "/blog") {
    screen = <BlogPage />;
  } else if (path.startsWith("/blog/")) {
    screen = <PostPage />;
  } else if (path === "/login") {
    screen = <LoginForm onSwitch={() => navigate("/cadastro")} />;
  } else if (path === "/cadastro") {
    screen = (
      <RegisterForm
        onSwitch={() => navigate("/login")}
        onSuccess={() => navigate("/onboarding")}
      />
    );
  } else if (path === "/delete_account") {
    screen = <DeleteAccountPage />;
  } else if (path === "/app" || path === "/app/appointments") {
    screen = (
      <AppShell>
        <AppointmentsPage />
      </AppShell>
    );
  } else if (path === "/app/clients") {
    screen = (
      <AppShell>
        <ClientsPage />
      </AppShell>
    );
  } else if (path === "/app/clients/new") {
    screen = (
      <AppShell>
        <NewClientPage />
      </AppShell>
    );
  } else if (path.startsWith("/app/clients/") && path.endsWith("/edit")) {
    screen = (
      <AppShell>
        <EditClientPage />
      </AppShell>
    );
  } else if (path.startsWith("/app/clients/")) {
    screen = (
      <AppShell>
        <ClientDetailPage />
      </AppShell>
    );
  } else if (path.startsWith("/app/plans/")) {
    screen = (
      <AppShell>
        <PlanDetailsPage />
      </AppShell>
    );
  } else if (path === "/app/paywall") {
    screen = (
      <AppShell>
        <PaywallPage />
      </AppShell>
    );
  } else if (path === "/app/employees") {
    screen = (
      <AppShell>
        <EmployeesPage />
      </AppShell>
    );
  } else if (path === "/app/blog") {
    screen = (
      <AppShell>
        <BlogAdminPage />
      </AppShell>
    );
  } else if (path === "/app/blog/new") {
    screen = (
      <AppShell>
        <BlogEditorPage />
      </AppShell>
    );
  } else if (path.startsWith("/app/blog/") && path.endsWith("/edit")) {
    screen = (
      <AppShell>
        <BlogEditorPage />
      </AppShell>
    );
  } else if (path.startsWith("/app/metrics")) {
    screen = (
      <AppShell>
        <MetricsRouter />
      </AppShell>
    );
  } else if (path === "/app/financial") {
    screen = (
      <AppShell>
        <FinancialPage />
      </AppShell>
    );
  } else if (path === "/app/assinatura") {
      screen = (
        <AppShell>
          <Suspense fallback={<div className="p-4">Carregando...</div>}>
            <SubscriptionPage />
          </Suspense>
        </AppShell>
      );
  } else if (path === "/app/settings") {
    screen = (
      <AppShell>
        <Suspense fallback={<div className="p-4">Carregando...</div>}>
          <SettingsPage />
        </Suspense>
      </AppShell>
    );
  } else if (path === "/app/settings/resources") {
    screen = (
      <AppShell>
        <Suspense fallback={<div className="p-4">Carregando...</div>}>
          <ResourcesPage />
        </Suspense>
      </AppShell>
    );
  } else if (path === "/app/settings/whatsapp") {
    screen = (
      <AppShell>
        <Suspense fallback={<div className="p-4">Carregando...</div>}>
          <WhatsappConfigPage />
        </Suspense>
      </AppShell>
    );
  } else if (path === "/app/settings/exceptions") {
    screen = (
      <AppShell>
        <Suspense fallback={<div className="p-4">Carregando...</div>}>
          <ScheduleExceptionsPage />
        </Suspense>
      </AppShell>
    );
  } else if (path === "/app/settings/contract") {
    screen = (
      <AppShell>
        <Suspense fallback={<div className="p-4">Carregando...</div>}>
          <ContractTemplatePage />
        </Suspense>
      </AppShell>
    );
  } else if (path === "/app/settings/insurance-providers") {
    screen = (
      <AppShell>
        <Suspense fallback={<div className="p-4">Carregando...</div>}>
          <InsuranceProvidersPage />
        </Suspense>
      </AppShell>
    );
  } else if (path === "/app/settings/company") {
    screen = (
      <AppShell>
        <Suspense fallback={<div className="p-4">Carregando...</div>}>
          <EditCompanyPage />
        </Suspense>
      </AppShell>
    );
  } else if (path === "/onboarding") {
    screen = <SetupCompanyPage />;
  } else {
    navigate("/login");
    screen = <></>;
  }

  const isApp = path.startsWith("/app");
  const isAuth = path === "/login" || path === "/cadastro";
  const isLanding = path === "/" || path === "/politica-e-termos";
  const isBlog = path === "/blog" || path.startsWith("/blog/");
  const isPublic = !!signingToken || !!rescheduleToken;

  let content: JSX.Element;
  if (isApp || isAuth || isLanding || isPublic) {
    content = screen;
  } else if (isBlog) {
    content = screen;
  } else {
    content = (
      <div className="min-h-screen w-full bg-white text-neutral-900 flex items-center justify-center p-6">
        <div className="w-full max-w-md">{screen}</div>
      </div>
    );
  }

  return (
    <>
      <Toaster richColors closeButton position="top-right" />
      {content}
    </>
  );
}
